import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, MapPin, Mail, MessageCircle } from "lucide-react";
import { AREAS, COLLECTIONS } from "@/lib/constants";
import { useLanguage } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const Footer = () => {
  const { t, localePath } = useLanguage();
  const { settings } = useSettings();
  const contact = settings?.contact || {};
  return (
    <footer className="bg-[var(--lux-ink)] text-[var(--lux-ivory)] mt-20" data-testid="site-footer">
      <div className="container-lux py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <div className="font-display text-2xl">MARBELLA</div>
            <div className="text-xs uppercase tracking-[0.3em] text-white/60 mb-4">Royal Villas</div>
            <p className="text-sm text-white/70 max-w-sm leading-relaxed">
              {t("footer.tagline")}
            </p>
            <div className="flex items-center gap-4 mt-6">
              <a href="https://instagram.com" aria-label="Instagram" className="text-white/70 hover:text-[var(--lux-champagne)] transition-colors"><Instagram className="h-5 w-5" /></a>
              <a href="https://facebook.com" aria-label="Facebook" className="text-white/70 hover:text-[var(--lux-champagne)] transition-colors"><Facebook className="h-5 w-5" /></a>
            </div>
            <div className="mt-6"><LanguageSwitcher /></div>
          </div>
          <div>
            <h4 className="text-sm font-semibold mb-4 text-white">{t("footer.areas")}</h4>
            <ul className="space-y-2">
              {AREAS.map((a) => (
                <li key={a.slug}><Link to={localePath(`/areas/${a.slug}`)} className="text-sm text-white/65 hover:text-white transition-colors">{a.name}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold mb-4 text-white">{t("footer.collections")}</h4>
            <ul className="space-y-2">
              {COLLECTIONS.slice(0, 7).map((c) => (
                <li key={c.slug}><Link to={localePath(`/collections/${c.slug}`)} className="text-sm text-white/65 hover:text-white transition-colors">{c.name}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold mb-4 text-white">{t("footer.contact")}</h4>
            <ul className="space-y-3 text-sm text-white/65">
              <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-[var(--lux-champagne)]" /> Golden Mile, Marbella, Spain</li>
              <li className="flex items-center gap-2"><MessageCircle className="h-4 w-4 text-[var(--lux-champagne)]" /> {contact.whatsapp_number ? `+${contact.whatsapp_number}` : "+34 676 777 884"}</li>
              <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-[var(--lux-champagne)]" /> {contact.email || "marbellaroyalvillas@gmail.com"}</li>
            </ul>
            <div className="mt-5 flex flex-col gap-1">
              <Link to={localePath("/about")} className="text-sm text-white/65 hover:text-white">{t("footer.aboutUs")}</Link>
              <Link to={localePath("/blog")} className="text-sm text-white/65 hover:text-white">{t("footer.guide")}</Link>
            </div>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/50">
          <span>&copy; {new Date().getFullYear()} Marbella Royal Villas &middot; Marbella Sierra Negra SL &middot; NIF B93123578 &middot; PATRYK SKOLIK. {t("footer.rights")}</span>
          <span>{t("footer.legal")}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
